import { datadogRum, type RumInitConfiguration } from '@datadog/browser-rum'
import { datadogLogs } from '@datadog/browser-logs'

const DATADOG_CONFIG: Omit<RumInitConfiguration, 'clientToken' | 'applicationId'> = {
  site: 'datadoghq.com',
  service: 'acepoker-ts-(homepage)',
  sessionSampleRate: Number(import.meta.env.VITE_DD_SESSION_SAMPLE_RATE || '100'),
  sessionReplaySampleRate: Number(import.meta.env.VITE_DD_SESSION_REPLAY_SAMPLE_RATE || '20'),
  trackUserInteractions: true,
  trackResources: true,
  trackLongTasks: true,
  defaultPrivacyLevel: 'mask-user-input',
  allowedTracingUrls: [], // to be added later
  enableExperimentalFeatures: ['feature_flags'],
  version: import.meta.env.VITE_APP_VERSION || '0.1.0'
}

let isInitialized = false

// Function to set user information in DataDog
export function setDatadogUser(playerId?: string, additionalInfo?: Record<string, unknown>) {
  if (import.meta.env.PROD && isInitialized) {
    datadogRum.setUser({
      id: playerId || 'anonymous',
      ...additionalInfo
    })
  }
}

export const initDataDog = (playerId?: string) => {
  // Only initialize DataDog in production environment
  if (import.meta.env.PROD && !isInitialized) {
    const appEnv = import.meta.env.VITE_APP_ENV || 'production'

    const applicationId = import.meta.env.VITE_DATADOG_APPLICATION_ID
    const clientToken = import.meta.env.VITE_DATADOG_CLIENT_TOKEN
    console.log('initDataDog', applicationId, clientToken)
    if (!applicationId || !clientToken) {
      console.warn('Datadog configuration is missing')
    } else {
      datadogRum.init({
        ...DATADOG_CONFIG,
        applicationId: applicationId,
        clientToken: clientToken,
        env: appEnv
      })

      datadogLogs.init({
        clientToken: clientToken,
        site: 'datadoghq.com',
        service: 'acepoker-ts-(homepage)',
        env: appEnv,
        forwardErrorsToLogs: true
      })

      isInitialized = true

      // Set user information if provided
      if (playerId) {
        setDatadogUser(playerId)
      }
    }
  }
}

export function trackError(error: Error, context?: Record<string, unknown>) {
  // Only track errors in production environment
  if (import.meta.env.PROD && isInitialized) {
    datadogRum.addError(error, {
      context: {
        ...context,
        timestamp: new Date().toISOString(),
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        url: typeof window !== 'undefined' ? window.location.href : undefined
      }
    })
  }
}

export function trackApiError(
  endpoint: string,
  error: Error,
  additionalContext?: Record<string, unknown>
) {
  console.log('trackApiError', endpoint, error, additionalContext)
  trackError(error, {
    errorType: 'api_error',
    endpoint,
    environment: import.meta.env.VITE_APP_ENV || 'production',
    ...additionalContext
  })
}

export function trackVueError(
  error: Error,
  errorInfo?: Record<string, unknown>,
  componentName?: string
) {
  trackError(error, {
    errorType: 'vue_error',
    component: componentName,
    errorInfo,
    environment: import.meta.env.VITE_APP_ENV || 'production'
  })
}

// Initialize Datadog with Flagsmith integration
export function initDatadogWithFlagsmith(datadogRumClient: typeof datadogRum) {
  return {
    client: datadogRumClient,
    trackTraits: true
  }
}
